<template>
  <v-main full-height class="overflow-y-auto" :class="{ 'bg-gray-50': !theme.current.value.dark }">
    <v-container class="flex flex-col px-5">
      <div class="mb-6">
        <div class="text-xl">
          {{ t("memberOnboarding.title", { accountName: memberInvitation?.accountName ?? "" }) }}
        </div>
        <div class="text-sm text-medium-emphasis">{{ t("memberOnboarding.description") }}</div>
      </div>

      <div v-if="loading" class="flex justify-center py-8">
        <v-progress-circular indeterminate color="primary" width="2" size="24" />
      </div>
      <template v-else-if="memberInvitation?.onboardingSubmittedAt">
        <v-alert
          v-if="member?.user.kyc?.kycStatus === 'REFUSED'"
          type="error"
          variant="tonal"
          :title="t('memberOnboarding.refused.title')"
          :text="t('memberOnboarding.refused.message')"
        />
        <v-card v-else class="flex items-center gap-4 p-6" elevation="3" rounded="lg">
          <v-icon icon="mdi-account-clock-outline" size="48" color="primary" />
          <div>
            <h3 class="text-lg font-semibold m-0 leading-tight">
              {{ t("memberOnboarding.inReview.title") }}
            </h3>
            <p class="text-sm text-medium-emphasis m-0">
              {{ t("memberOnboarding.inReview.message") }}
            </p>
          </div>
        </v-card>
      </template>
      <v-stepper
        v-else-if="member"
        :model-value="step"
        class="bg-transparent"
        flat
        editable
        hide-actions
        mobile-breakpoint="md"
        @update:model-value="goTo"
      >
        <v-stepper-header class="shadow-none">
          <template v-for="(item, index) in steps" :key="item.value">
            <v-divider v-if="index > 0" />
            <v-stepper-item
              :value="item.value"
              :title="item.title"
              :complete="item.complete"
              :error="item.missing"
              :subtitle="item.missing ? t('treezorOnboarding.steps.missingInformation') : undefined"
              :color="item.complete ? 'success' : 'primary'"
            />
          </template>
        </v-stepper-header>

        <!-- Padding keeps step card shadows inside the window's overflow clip. -->
        <v-stepper-window class="mx-0 mb-0 p-1">
          <v-stepper-window-item :value="1">
            <v-alert v-if="identityLocked" class="mb-6" type="success" variant="tonal">
              {{ t("memberOnboarding.identityVerified") }}
            </v-alert>
            <v-form
              id="member-form"
              ref="memberForm"
              :disabled="saving || identityLocked"
              class="flex flex-col gap-6"
              @submit.prevent="save"
            >
              <OpeningCard :title="t('treezorOnboarding.accountCreator.sections.identity')">
                <PersonIdentityFields
                  v-model="form"
                  :missing-fields="leftSteps.has(1) ? missingFields : []"
                >
                  <template #prepend>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="form.title"
                        :items="civilityItems"
                        :rules="[
                          required,
                          (value: string) =>
                            value === 'M' ||
                            value === 'MME' ||
                            value === 'MX' ||
                            t('errors.required'),
                        ]"
                        :error-messages="
                          leftSteps.has(1) && missingFields.includes('title') && !form.title
                            ? t('errors.required')
                            : []
                        "
                      >
                        <template #label>
                          {{ t("treezorOnboarding.effectiveBeneficiaries.civility") }}
                          <span class="text-error">*</span>
                        </template>
                      </v-select>
                    </v-col>
                  </template>
                  <v-col cols="12">
                    <div
                      :class="specifiedUSPersonMissing() ? 'text-error' : 'text-medium-emphasis'"
                    >
                      {{ t("treezorOnboarding.accountCreator.specifiedUSPersonQuestion") }}
                      <span class="text-error">*</span>
                    </div>
                    <v-radio-group
                      v-model="form.specifiedUSPerson"
                      color="primary"
                      inline
                      hide-details="auto"
                      :error="specifiedUSPersonMissing()"
                      :error-messages="specifiedUSPersonMissing() ? t('errors.required') : []"
                      :rules="validationActive() ? [answered] : []"
                      :aria-label="t('treezorOnboarding.accountCreator.specifiedUSPersonQuestion')"
                    >
                      <v-radio :label="t('treezorOnboarding.sanctions.yes')" :value="true" />
                      <v-radio :label="t('treezorOnboarding.sanctions.no')" :value="false" />
                    </v-radio-group>
                  </v-col>
                </PersonIdentityFields>
              </OpeningCard>
              <OpeningCard :title="t('treezorOnboarding.sections.contact')">
                <PersonContactFields
                  v-model="form"
                  :missing-fields="leftSteps.has(1) ? missingFields : []"
                />
              </OpeningCard>
            </v-form>
            <div class="mt-6 flex justify-end gap-2">
              <v-btn
                v-if="!identityLocked"
                type="submit"
                form="member-form"
                color="primary"
                variant="flat"
                prepend-icon="mdi-content-save"
                append-icon="mdi-chevron-right"
                :loading="saving"
                :disabled="saving"
              >
                {{ t("common.next") }}
              </v-btn>
              <v-btn
                v-else
                color="primary"
                variant="flat"
                append-icon="mdi-chevron-right"
                @click="goTo(2)"
              >
                {{ t("common.next") }}
              </v-btn>
            </div>
          </v-stepper-window-item>

          <v-stepper-window-item :value="2">
            <OpeningCard
              :title="t('treezorOnboarding.accountCreator.liveness.title')"
              :subtitle="t('treezorOnboarding.accountCreator.liveness.hint')"
            >
              <v-chip
                v-if="member.kycLivenessStatus === 'verified'"
                size="large"
                color="success"
                variant="tonal"
                prepend-icon="mdi-check"
              >
                {{ t("treezorOnboarding.accountCreator.liveness.verified") }}
              </v-chip>
              <template v-else>
                <div
                  v-if="member.kycLivenessStatus === 'started'"
                  class="mb-3 flex items-center gap-2 text-sm text-medium-emphasis"
                >
                  <v-progress-circular indeterminate size="20" width="2" color="primary" />
                  {{ t("treezorOnboarding.accountCreator.liveness.started") }}
                </div>
                <v-alert
                  v-else-if="member.kycLivenessStatus !== 'none'"
                  class="mb-3"
                  type="error"
                  color="error"
                >
                  <p class="font-semibold m-0">
                    {{ t(`treezorOnboarding.accountCreator.liveness.${member.kycLivenessStatus}`) }}
                  </p>
                  <ul v-if="member.kycLivenessReasonCodes.length" class="m-0 ps-4">
                    <li v-for="code in member.kycLivenessReasonCodes" :key="code">
                      {{ t(`treezorOnboarding.accountCreator.liveness.reasons.${code}`) }}
                    </li>
                  </ul>
                </v-alert>
                <v-btn
                  v-if="member.kycLivenessStatus !== 'started'"
                  color="primary"
                  variant="flat"
                  prepend-icon="mdi-play"
                  :disabled="missingFields.length > 0"
                  @click="startKycLiveness"
                >
                  {{
                    t(
                      member.kycLivenessStatus === "none"
                        ? "treezorOnboarding.accountCreator.liveness.start"
                        : "treezorOnboarding.accountCreator.liveness.retry",
                    )
                  }}
                </v-btn>
                <p v-if="leftSteps.has(2)" class="text-sm text-error mt-2 mb-0">
                  {{ t("treezorOnboarding.accountCreator.liveness.missing") }}
                </p>
              </template>
            </OpeningCard>
            <div class="mt-6 flex justify-end gap-2">
              <v-btn
                class="me-auto"
                variant="text"
                prepend-icon="mdi-chevron-left"
                @click="goTo(1)"
              >
                {{ t("common.previous") }}
              </v-btn>
              <v-btn
                color="primary"
                variant="flat"
                append-icon="mdi-chevron-right"
                @click="goTo(3)"
              >
                {{ t("common.next") }}
              </v-btn>
            </div>
          </v-stepper-window-item>

          <v-stepper-window-item :value="3">
            <v-alert
              v-if="!member.powerOfAttorneyRequired"
              type="success"
              variant="tonal"
              :title="t('memberOnboarding.powerOfAttorney.title')"
              :text="t('memberOnboarding.powerOfAttorney.notRequired')"
            />
            <OpeningCard
              v-else
              :title="t('memberOnboarding.powerOfAttorney.title')"
              :subtitle="t('memberOnboarding.powerOfAttorney.hint')"
            >
              <div class="flex flex-col gap-3">
                <v-card
                  v-if="member.powerOfAttorneyDocument"
                  variant="outlined"
                  rounded="lg"
                  class="flex items-center gap-2 px-3 py-3 border-theme sm:gap-3 sm:px-4"
                >
                  <v-icon icon="mdi-file-document-outline" class="text-medium-emphasis" />
                  <div class="grow min-w-0">
                    <div class="text-sm font-medium">
                      {{ t("treezorOnboarding.documents.powerOfAttorney") }}
                    </div>
                    <div class="text-xs text-medium-emphasis">
                      {{
                        t("treezorOnboarding.documents.uploadedOn", {
                          date: new Date(member.powerOfAttorneyDocument.createdAt).toLocaleString(
                            locale,
                            { dateStyle: "short", timeStyle: "short" },
                          ),
                        })
                      }}
                    </div>
                  </div>
                </v-card>
                <v-file-input
                  v-model="powerOfAttorneyFile"
                  density="compact"
                  variant="outlined"
                  prepend-inner-icon="mdi-paperclip"
                  :prepend-icon="''"
                  hide-details="auto"
                  accept=".jpg,.jpeg,.png,.svg,.tif,.tiff,.pdf,image/jpeg,image/png,image/svg+xml,image/tiff,application/pdf"
                  :rules="[maxFileSize(50)]"
                  :error-messages="
                    leftSteps.has(3) && !member.powerOfAttorneyDocument ? t('errors.required') : []
                  "
                  :loading="uploading"
                  :disabled="uploading || submitting"
                  :label="t('treezorOnboarding.documents.chooseFile')"
                  @update:model-value="uploadPowerOfAttorney"
                />
              </div>
            </OpeningCard>
            <div class="mt-6 flex justify-end gap-2">
              <v-btn
                class="me-auto"
                variant="text"
                prepend-icon="mdi-chevron-left"
                :disabled="submitting"
                @click="goTo(2)"
              >
                {{ t("common.previous") }}
              </v-btn>
              <v-btn
                color="primary"
                variant="flat"
                prepend-icon="mdi-send"
                :loading="submitting"
                :disabled="submitting || uploading"
                @click="submit"
              >
                {{ t("common.send") }}
              </v-btn>
            </div>
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
    </v-container>
  </v-main>
</template>

<script setup lang="ts">
import { useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useOpenInNewTab } from "@/composables/useOpenInNewTab";
import { useRules } from "@/composables/useRules";
import { call, db } from "@/firebase";
import { useRedirect } from "@/router/redirect";
import { useToast } from "@/store";
import { isTreezorDocumentFile, postTreezorDocumentUpload } from "@/utils/treezorDocumentUpload";
import OpeningCard from "@/views/Account/views/Opening/OpeningCard.vue";
import PersonContactFields from "@/views/Account/views/Opening/PersonContactFields.vue";
import PersonIdentityFields from "@/views/Account/views/Opening/PersonIdentityFields.vue";
import {
  accountCreatorFormFrom,
  emptyAccountCreatorForm,
} from "@/views/Account/views/Opening/useTreezorOnboarding";
import {
  accountMemberInvitationFullPath,
  type AccountMemberInvitation,
} from "@aircap/common/accountMemberInvitation";
import type { CallablesMap } from "@aircap/common/callables";
import { TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS } from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { useIntervalFn } from "@vueuse/core";
import { doc } from "firebase/firestore";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useTheme } from "vuetify";
import type { VForm } from "vuetify/components";

const props = defineProps<{
  memberInvitationId: string;
}>();

const { t, locale } = useI18n();
const router = useRouter();
const { redirectToHome } = useRedirect();
const theme = useTheme();
const { maxFileSize, required } = useRules();
const { showErrorToast, showSuccessToast } = useToast();
const openInNewTab = useOpenInNewTab();

const memberInvitation = useDocument<AccountMemberInvitation>(
  computed(() => doc(db, accountMemberInvitationFullPath(props.memberInvitationId))),
);
// syncTreezorUser deletes the invitation once the member's KYC is validated: the account opens if
// they are now its member, otherwise the router sends them home. An invitation already gone (an
// old email link) sends them home too.
watch([memberInvitation, memberInvitation.pending], ([current, pending], [previous]) => {
  if (pending || current) return;
  if (previous) void router.replace({ name: "Account", params: { accountId: previous.accountId } });
  else void redirectToHome({ replace: true });
});
const member = ref<CallablesMap["getTreezorAccountMember"]["response"]>();
const loading = ref(false);
const form = ref(emptyAccountCreatorForm());
const formTried = ref(false);

const identityLocked = computed(
  () =>
    member.value?.user.kyc?.kycStatus === "PENDING" ||
    member.value?.user.kyc?.kycStatus === "VALIDATED",
);
const missingFields = computed(() =>
  TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS.filter((field) => {
    const value = member.value?.user[field];
    return value === undefined || value === null || value === "";
  }),
);
const civilityItems = computed(() =>
  (["M", "MME", "MX"] as const).map((value) => ({
    value,
    title: t(`treezorOnboarding.effectiveBeneficiaries.civilities.${value}`),
  })),
);

const step = ref(1);
const leftSteps = ref(new Set<number>());
const steps = computed(() =>
  [
    {
      value: 1,
      title: t("treezorOnboarding.steps.you"),
      complete: identityLocked.value || missingFields.value.length === 0,
    },
    {
      value: 2,
      title: t("treezorOnboarding.steps.identity"),
      complete: identityLocked.value || member.value?.kycLivenessStatus === "verified",
    },
    {
      value: 3,
      title: t("memberOnboarding.steps.powerOfAttorney"),
      complete: !member.value?.powerOfAttorneyRequired || !!member.value.powerOfAttorneyDocument,
    },
  ].map((item) => ({ ...item, missing: leftSteps.value.has(item.value) && !item.complete })),
);
const goTo = (target: number) => {
  for (let value = 1; value < target; value++) leftSteps.value.add(value);
  leftSteps.value.add(step.value);
  step.value = target;
};

const load = async () => {
  try {
    member.value = await call("getTreezorAccountMember")({
      memberInvitationId: props.memberInvitationId,
    });
  } catch (cause) {
    const error = new Error(
      `Error loading member onboarding of member invitation ${props.memberInvitationId}`,
      {
        cause,
      },
    );
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};
watch(
  () => props.memberInvitationId,
  async () => {
    step.value = 1;
    leftSteps.value.clear();
    formTried.value = false;
    loading.value = true;
    await load();
    loading.value = false;
    if (member.value) {
      form.value = accountCreatorFormFrom(member.value.user, {
        isLegalRepresentative: false,
        isEffectiveBeneficiary: false,
        relationship: null,
      });
      // Every identity form requires a civility, so once one is stored, Treezor's
      // specifiedUSPerson 0 was answered rather than left at its creation default.
      if (member.value.user.title)
        form.value.specifiedUSPerson = member.value.user.specifiedUSPerson === 1;
    }
    if (identityLocked.value) step.value = 3;
  },
  { immediate: true },
);

const validationActive = () => leftSteps.value.has(1) || formTried.value;
const specifiedUSPersonMissing = () => validationActive() && form.value.specifiedUSPerson === null;
const answered = (value: unknown) =>
  (value !== null && value !== undefined) || t("errors.required");

const memberForm = ref<VForm>();
const saving = ref(false);
const save = async () => {
  formTried.value = true;
  const result = await memberForm.value?.validate();
  if (
    !result?.valid ||
    form.value.specifiedUSPerson === null ||
    (form.value.title !== "M" && form.value.title !== "MME" && form.value.title !== "MX")
  )
    return;

  saving.value = true;
  try {
    await call("updateTreezorAccountMember")({
      memberInvitationId: props.memberInvitationId,
      specifiedUSPerson: form.value.specifiedUSPerson,
      title: form.value.title,
      firstname: form.value.firstname,
      lastname: form.value.lastname,
      middleNames: form.value.middleNames,
      birthday: form.value.birthday,
      placeOfBirth: form.value.placeOfBirth,
      birthCountry: form.value.birthCountry ?? "",
      nationality: form.value.nationality ?? "",
      phone: form.value.phone,
      address1: form.value.address1,
      address2: form.value.address2,
      address3: form.value.address3,
      postcode: form.value.postcode,
      city: form.value.city,
      country: form.value.country ?? "",
    });
    await load();
    showSuccessToast(t("treezorOnboarding.accountCreator.updateSuccess"));
    goTo(2);
  } catch (cause) {
    const error = new Error(
      `Error saving member identity of member invitation ${props.memberInvitationId}`,
      {
        cause,
      },
    );
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    saving.value = false;
  }
};

let kycLivenessRequestedAt: number | undefined;
let kycLivenessPreviousStartedAt: number | null = null;
let kycLivenessPollingStartedAt = 0;
const kycLivenessPolling = useIntervalFn(
  () => {
    if (!member.value) return;
    if (Date.now() - kycLivenessPollingStartedAt >= 60 * 60 * 1000) {
      member.value.kycLivenessStatus = "none";
      return;
    }
    call("getTreezorAccountMemberKycLiveness")({ memberInvitationId: props.memberInvitationId })
      .then((result) => {
        // The previous attempt's result can come back until Treezor registers the new one.
        const awaitingNewAttempt =
          kycLivenessRequestedAt !== undefined &&
          Date.now() - kycLivenessRequestedAt < 2 * 60 * 1000 &&
          result.kycLivenessStatus !== "verified" &&
          result.kycLivenessStartedAt === kycLivenessPreviousStartedAt;
        if (!awaitingNewAttempt && member.value) member.value = { ...member.value, ...result };
      })
      .catch((cause: unknown) => {
        const error = new Error("Error polling Treezor KYC liveness status", { cause });
        console.error(error);
        captureException(error);
      });
  },
  10_000,
  { immediate: false },
);
watch(
  () => member.value?.kycLivenessStatus,
  (status) => {
    if (status === "started") {
      kycLivenessPollingStartedAt = Date.now();
      kycLivenessPolling.resume();
    } else kycLivenessPolling.pause();
  },
);

const startKycLiveness = () => {
  if (
    !member.value ||
    !openInNewTab("treezorAccountMemberKycLiveness", {
      memberInvitationId: props.memberInvitationId,
    })
  )
    return;
  kycLivenessRequestedAt = Date.now();
  kycLivenessPreviousStartedAt = member.value.kycLivenessStartedAt;
  member.value.kycLivenessStatus = "started";
  member.value.kycLivenessReasonCodes = [];
};

const powerOfAttorneyFile = ref<File | null>(null);
const uploading = ref(false);
const uploadPowerOfAttorney = async (file: File | File[] | null) => {
  if (!file || Array.isArray(file)) return;
  if (!isTreezorDocumentFile(file)) {
    showErrorToast(t("treezorOnboarding.documents.invalidFileType"));
    powerOfAttorneyFile.value = null;
    return;
  }
  if (file.size > 50 * 1024 * 1024) {
    showErrorToast(t("errors.fileTooBig", { maxFileSizeMB: 50 }));
    powerOfAttorneyFile.value = null;
    return;
  }

  uploading.value = true;
  try {
    const upload = await call("createTreezorPowerOfAttorneyUpload")({
      memberInvitationId: props.memberInvitationId,
    });
    await postTreezorDocumentUpload(upload, file);
    // Treezor lists the document a moment after the upload, and the opaque S3 response
    // cannot tell a rejected upload apart, so only the listing confirms it.
    let listed = false;
    for (let attempt = 0; attempt < 8 && !listed; attempt++) {
      if (attempt) await new Promise((resolve) => setTimeout(resolve, 1000));
      const { powerOfAttorneyDocument } = await call(
        "getTreezorAccountMemberPowerOfAttorneyDocument",
      )({ memberInvitationId: props.memberInvitationId });
      if (member.value) member.value = { ...member.value, powerOfAttorneyDocument };
      listed = powerOfAttorneyDocument?.documentId === upload.documentId;
    }
    if (!listed) throw new Error(`Treezor did not list uploaded document ${upload.documentId}`);
    showSuccessToast(t("treezorOnboarding.documents.uploadSuccess"));
  } catch (cause) {
    const error = new Error(
      `Error uploading power of attorney of member invitation ${props.memberInvitationId}`,
      {
        cause,
      },
    );
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    powerOfAttorneyFile.value = null;
    uploading.value = false;
  }
};

const submitting = ref(false);
const submit = async () => {
  goTo(3);
  if (steps.value.some((item) => !item.complete)) {
    step.value = steps.value.find((item) => !item.complete)?.value ?? 3;
    return;
  }

  submitting.value = true;
  try {
    await call("submitTreezorAccountMemberOnboarding")({
      memberInvitationId: props.memberInvitationId,
    });
    await load();
  } catch (cause) {
    const error = new Error(
      `Error submitting member onboarding of member invitation ${props.memberInvitationId}`,
      {
        cause,
      },
    );
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    submitting.value = false;
  }
};
</script>
